// Data hook for the Phase 4C report screens: de-duplicates identical requests, cancels the previous request when the key changes (so a slow answer for
// filters the user already left can never overwrite a newer one), and keeps a short-lived in-memory cache so screens that need the same list do not
// request it again on every navigation.
//
//   const { data, error, loading, reload } = useReportQuery(key, (signal) => listReport(kind, filters, { token, signal }), { enabled })
//
// `key`: a string built with reportCacheKey (it contains the token tail, so a different account never sees another account's cache). The fetcher
// resolves to { ok, status, data, aborted? } (utils/api/http.js). Errors are not cached. Call invalidateReportCache(prefix) after a write.
import { useCallback, useEffect, useRef, useState } from "react";

const DEFAULT_TTL_MS = 60 * 1000;
const cache = new Map(); // key -> { at, result }
const inflight = new Map(); // key -> Promise

export function reportCacheKey(token, kind, filters = {}) {
  const tail = String(token || "").slice(-8);
  const stable = Object.keys(filters).sort().map((name) => `${name}=${JSON.stringify(filters[name])}`).join("&");
  return `${tail}|${kind}|${stable}`;
}

export function invalidateReportCache(prefix = "") {
  for (const key of [...cache.keys()]) if (key.includes(prefix)) cache.delete(key);
}
export const clearReportCache = () => cache.clear();

export default function useReportQuery(key, fetcher, { enabled = true, ttlMs = DEFAULT_TTL_MS } = {}) {
  const [state, setState] = useState({ data: null, error: null, loading: Boolean(enabled) });
  const fetcherRef = useRef(fetcher);
  fetcherRef.current = fetcher;
  const [reloads, setReloads] = useState(0);
  const forceRef = useRef(false);

  useEffect(() => {
    if (!enabled || !key) {
      setState((current) => ({ ...current, loading: false }));
      return undefined;
    }
    const force = forceRef.current;
    forceRef.current = false;
    const cached = cache.get(key);
    if (!force && cached && Date.now() - cached.at < ttlMs) {
      setState({ data: cached.result.data, error: null, loading: false });
      return undefined;
    }

    const controller = new AbortController();
    let active = true;
    setState((current) => ({ data: current.data, error: null, loading: true }));

    let promise = !force ? inflight.get(key) : null;
    if (!promise) {
      promise = Promise.resolve(fetcherRef.current(controller.signal)).finally(() => {
        if (inflight.get(key) === promise) inflight.delete(key);
      });
      inflight.set(key, promise);
    }
    promise.then((result) => {
      if (!active || (result && result.aborted)) return;
      if (result && result.ok) {
        cache.set(key, { at: Date.now(), result });
        setState({ data: result.data, error: null, loading: false });
      } else {
        setState({ data: null, error: { status: result ? result.status : 0, message: result && result.error }, loading: false });
      }
    });
    return () => {
      active = false;
      controller.abort();
      if (inflight.get(key) === promise) inflight.delete(key);
    };
  }, [key, enabled, ttlMs, reloads]);

  const reload = useCallback(() => {
    forceRef.current = true;
    setReloads((count) => count + 1);
  }, []);

  return { ...state, reload };
}
